import { Link, useParams } from "react-router-dom";
import SiteNav from "@/components/feature/SiteNav";
import SiteFooter from "@/components/feature/SiteFooter";
import StartProjectCTA from "@/components/feature/StartProjectCTA";
import { getService } from "@/mocks/services";
import { getProject } from "@/mocks/projects";

const PROCESS = [
  { num: "01", title: "Discover", body: "Site visit, brief and feasibility." },
  { num: "02", title: "Design", body: "Concept, plan and material direction." },
  { num: "03", title: "Plan", body: "Documentation, permits and pricing." },
  { num: "04", title: "Build", body: "Construction managed by the same team." },
  { num: "05", title: "Complete", body: "Handover, walkthrough and aftercare." },
];

export default function ServiceDetail() {
  const { slug = "" } = useParams();
  const svc = getService(slug);

  if (!svc) {
    return (
      <div className="min-h-screen bg-warm-bg text-ink">
        <SiteNav />
        <div className="pt-40 pb-24 w-full" style={{ paddingLeft: "clamp(20px, 5vw, 96px)", paddingRight: "clamp(20px, 5vw, 96px)" }}>
          <h1 className="font-serif-display font-light" style={{ fontSize: "44px" }}>Service not found.</h1>
          <Link to="/services" className="arrow-link mt-8 inline-flex text-[12px] tracking-[0.12em] uppercase cursor-pointer">
            Back to Services
            <i className="ri-arrow-right-line arrow w-4 h-4 flex items-center justify-center" />
          </Link>
        </div>
        <SiteFooter />
      </div>
    );
  }

  const related = svc.relatedProjectSlugs.map(getProject).filter(Boolean) as NonNullable<ReturnType<typeof getProject>>[];

  return (
    <div className="min-h-screen bg-warm-bg text-ink">
      <SiteNav />

      <section className="w-full pt-[112px] md:pt-[144px] pb-14">
        <div className="w-full" style={{ paddingLeft: "clamp(20px, 5vw, 96px)", paddingRight: "clamp(20px, 5vw, 96px)" }}>
          <div className="flex items-center gap-2 text-[11px] tracking-[0.12em] uppercase text-ink-soft mb-8">
            <Link to="/" className="hover:text-accent cursor-pointer">Home</Link><span>/</span>
            <Link to="/services" className="hover:text-accent cursor-pointer">Services</Link><span>/</span>
            <span>{svc.title}</span>
          </div>
          <span className="eyebrow">Service — {svc.num}</span>
          <h1 className="mt-5 font-serif-display font-light leading-[1.02]"
              style={{ fontSize: "clamp(38px,4vw,54px)", letterSpacing: "-0.02em" }}>
            {svc.title}
          </h1>
          <p className="mt-8 max-w-[640px] text-ink-soft leading-[1.65]" style={{ fontSize: "17px" }}>
            {svc.positioning}
          </p>
        </div>
      </section>

      <section className="w-full">
        <img src={svc.image} alt={svc.title}
             className="w-screen block" style={{ aspectRatio: "16/9", objectFit: "cover" }} />
      </section>

      <section className="w-full py-24 md:py-28">
        <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16" style={{ paddingLeft: "clamp(20px, 5vw, 96px)", paddingRight: "clamp(20px, 5vw, 96px)" }}>
          <div>
            <span className="eyebrow">What we do</span>
            <ul className="mt-6 flex flex-col">
              {svc.whatWeDo.map((item, i) => (
                <li key={i} className="border-t hairline py-4 text-[16px] flex gap-4">
                  <span className="eyebrow" style={{ minWidth: "28px" }}>{String(i + 1).padStart(2, "0")}</span>
                  <span>{item}</span>
                </li>
              ))}
              <li className="border-t hairline" />
            </ul>
          </div>
          <div>
            <span className="eyebrow">Our approach</span>
            <p className="mt-6 font-serif-display font-light leading-[1.3]"
               style={{ fontSize: "clamp(22px,1.8vw,28px)" }}>
              {svc.approach}
            </p>
          </div>
        </div>
      </section>

      <section className="w-full pb-24">
        <img src={svc.secondaryImage ?? svc.image} alt={`${svc.title} — detail`}
             className="w-screen block" style={{ aspectRatio: "16/9", objectFit: "cover" }} />
      </section>

      <section className="w-full pb-24 md:pb-32">
        <div className="w-full" style={{ paddingLeft: "clamp(20px, 5vw, 96px)", paddingRight: "clamp(20px, 5vw, 96px)" }}>
          <span className="eyebrow">Relevant process</span>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-5 gap-6">
            {PROCESS.map(p => (
              <div key={p.num} className="border-t hairline pt-5">
                <span className="eyebrow">{p.num}</span>
                <div className="mt-3 font-serif-display font-light" style={{ fontSize: "22px" }}>{p.title}</div>
                <p className="mt-2 text-ink-soft leading-[1.6]" style={{ fontSize: "14px" }}>{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="w-full pb-24 md:pb-32">
          <div className="w-full mb-10" style={{ paddingLeft: "clamp(20px, 5vw, 96px)", paddingRight: "clamp(20px, 5vw, 96px)" }}>
            <div className="flex items-end justify-between">
              <span className="eyebrow">Related Projects</span>
              <Link to="/projects" className="arrow-link text-[12px] tracking-[0.12em] uppercase cursor-pointer">
                All Projects
                <i className="ri-arrow-right-line arrow w-4 h-4 flex items-center justify-center" />
              </Link>
            </div>
          </div>
          <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-0">
            {related.map(p => (
              <Link key={p.slug} to={`/projects/${p.slug}`}
                    className="group cursor-pointer block [&_.meta]:px-[clamp(20px,5vw,96px)]">
                <div className="overflow-hidden">
                  <img src={p.image} alt={p.title}
                       className="w-full block transition-transform duration-700 group-hover:scale-[1.02]"
                       style={{ aspectRatio: "4/3", objectFit: "cover" }} />
                </div>
                <div className="meta mt-4 mb-10 flex items-baseline justify-between gap-4">
                  <div>
                    <span className="eyebrow">{p.category}</span>
                    <div className="mt-2 font-serif-display font-light" style={{ fontSize: "24px" }}>{p.title}</div>
                  </div>
                  <span className="text-ink-soft" style={{ fontSize: "13px" }}>{p.year}</span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <StartProjectCTA />
      <SiteFooter />
    </div>
  );
}